import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useGuestStore } from '../stores/guestStore'
import { UserPlus, Eye, EyeOff, Mail, ArrowLeft } from 'lucide-react'
import { BrandWordmark } from '../components/brand/BrandMark'

const RESEND_COOLDOWN_SECONDS = 60

// The API normally returns `error` as a string, but a server-side validation
// failure can surface a non-string (e.g. a Zod issue array with `{ code }`
// objects). Rendering a non-string as a React child crashes the page
// (React error #31), so always coerce the server payload to a readable string
// before putting it in state.
function errorText(data: unknown, fallback: string): string {
  const e = (data as { error?: unknown } | null | undefined)?.error
  if (typeof e === 'string' && e.trim()) return e
  if (Array.isArray(e)) {
    const msg = e
      .map((x) => (typeof x === 'string' ? x : (x as { message?: unknown })?.message))
      .filter((m): m is string => typeof m === 'string' && m.length > 0)
      .join(', ')
    if (msg) return msg
  }
  if (e && typeof (e as { message?: unknown }).message === 'string' && (e as { message: string }).message.trim()) {
    return (e as { message: string }).message
  }
  return fallback
}

export default function Register() {
  const [searchParams] = useSearchParams()
  const redirect = searchParams.get('redirect') || '/channels/@me'
  const inviteFromUrl = searchParams.get('invite') || ''
  const emailFromUrl = searchParams.get('email') || ''
  const [email, setEmail] = useState(emailFromUrl)
  const [username, setUsername] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [position, setPosition] = useState('')
  const [birthday, setBirthday] = useState('')
  const [password, setPassword] = useState('')
  const [inviteCode, setInviteCode] = useState('')
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Two-phase signup: fill the form -> we email a 6-digit code -> enter the
  // code -> the account is created + auto-login. The account does NOT exist
  // until the code is confirmed (see authStore.registerVerify).
  const [phase, setPhase] = useState<'form' | 'code'>('form')
  const [sessionId, setSessionId] = useState('')
  const [code, setCode] = useState('')
  const [resendIn, setResendIn] = useState(0)

  const registerStart = useAuthStore((s) => s.registerStart)
  const registerVerify = useAuthStore((s) => s.registerVerify)
  const registerResend = useAuthStore((s) => s.registerResend)
  const isGuest = useGuestStore((s) => s.isGuest)
  const navigate = useNavigate()

  useEffect(() => {
    if (resendIn <= 0) return
    const t = setInterval(() => setResendIn((n) => (n > 0 ? n - 1 : 0)), 1000)
    return () => clearInterval(t)
  }, [resendIn])

  const handleStart = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    const invite = inviteFromUrl || inviteCode
    if (!invite) {
      setError('Invite code is required')
      return
    }
    // Both names are mandatory — trim-check here so whitespace-only entries
    // don't slip past the native `required` attribute. Server enforces the
    // same rule (registerSchema: firstName/lastName .trim().min(1)).
    if (!firstName.trim() || !lastName.trim()) {
      setError('First and last name are required')
      return
    }
    setSubmitting(true)
    try {
      const res = await registerStart(email, username, firstName.trim(), lastName.trim(), position, birthday, password, invite)
      setSessionId(res.sessionId)
      setCode('')
      setPhase('code')
      setResendIn(RESEND_COOLDOWN_SECONDS)
    } catch (err: any) {
      setError(errorText(err.response?.data, 'Registration failed'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from your email')
      return
    }
    setSubmitting(true)
    try {
      // Clear any guest session right before we authenticate the new user.
      useGuestStore.getState().clearGuest()
      await registerVerify(sessionId, code)
      navigate(redirect)
    } catch (err: any) {
      const status = err.response?.status
      const data = err.response?.data
      if (status === 410 || status === 429) {
        // Expired or locked out -> bounce back to the form to start over.
        setPhase('form')
        setSessionId('')
        setError(errorText(data, 'Code expired. Please start again.'))
      } else if (data?.attemptsLeft != null) {
        setError(`Incorrect code. ${data.attemptsLeft} attempt${data.attemptsLeft === 1 ? '' : 's'} left.`)
      } else {
        setError(errorText(data, 'Verification failed'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleResend = async () => {
    if (resendIn > 0) return
    setError('')
    try {
      await registerResend(sessionId)
      setResendIn(RESEND_COOLDOWN_SECONDS)
    } catch (err: any) {
      const status = err.response?.status
      const data = err.response?.data
      if (status === 410) {
        setPhase('form')
        setSessionId('')
        setError(errorText(data, 'Session expired. Please start again.'))
      } else if (data?.retryAfterMs != null) {
        setResendIn(Math.ceil(data.retryAfterMs / 1000))
      } else {
        setError(errorText(data, 'Could not resend the code'))
      }
    }
  }

  const backToForm = () => {
    setPhase('form')
    setSessionId('')
    setCode('')
    setError('')
  }

  // Registration is NOT public. The only ways in are an emailed invite link
  // (/register?invite=CODE) or a guest converting their session to an account.
  // A cold visit with neither is bounced to login — there is no public sign-up.
  if (!inviteFromUrl && !isGuest) {
    return <Navigate to="/login" replace />
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card border border-border rounded-xl p-8 shadow-lg">
        {/* White label §4.1 — signup is a branded surface too. */}
        <BrandWordmark className="mb-6" />

        {phase === 'form' ? (
          <>
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-foreground">Create an account</h1>
              <p className="text-muted-foreground text-sm mt-1">Invitation only</p>
            </div>
            <form onSubmit={handleStart} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">Invite Code</label>
                <input
                  type="text"
                  value={inviteFromUrl || inviteCode}
                  onChange={(e) => { if (!inviteFromUrl) setInviteCode(e.target.value) }}
                  readOnly={!!inviteFromUrl}
                  className={`w-full bg-card text-foreground rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary border border-border ${inviteFromUrl ? 'opacity-70' : ''}`}
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { if (!emailFromUrl) setEmail(e.target.value) }}
                  readOnly={!!emailFromUrl}
                  className={`w-full bg-card text-foreground rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary border border-border ${emailFromUrl ? 'opacity-70' : ''}`}
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full bg-card text-foreground rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary border border-border"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">First Name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full bg-card text-foreground rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary border border-border"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full bg-card text-foreground rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary border border-border"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">Position / Job title (optional)</label>
                <input
                  type="text"
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  placeholder="e.g. Senior Engineer, QA Lead"
                  className="w-full bg-card text-foreground rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary border border-border"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">Date of Birth</label>
                <input
                  type="date"
                  value={birthday}
                  onChange={(e) => setBirthday(e.target.value)}
                  className="w-full bg-card text-foreground rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary border border-border"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-card text-foreground rounded px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary border border-border"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              {error && <p className="text-destructive text-sm">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground font-medium py-2.5 rounded transition flex items-center justify-center gap-2"
              >
                <UserPlus size={18} />
                {submitting ? 'Sending code…' : 'Continue'}
              </button>
            </form>
            <p className="mt-4 text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to={`/login${redirect !== '/channels/@me' ? `?redirect=${encodeURIComponent(redirect)}` : ''}`} className="text-primary hover:underline">
                Log In
              </Link>
            </p>
          </>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/15">
                <Mail size={22} className="text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-foreground">Check your email</h1>
              <p className="text-muted-foreground text-sm mt-1">
                We sent a 6-digit code to{' '}
                <span className="text-foreground font-medium">{email}</span>
              </p>
            </div>
            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">Verification code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  autoFocus
                  className="w-full bg-card text-foreground rounded px-3 py-3 text-center text-lg tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-primary border border-border"
                  required
                />
              </div>
              {error && <p className="text-destructive text-sm">{error}</p>}
              <button
                type="submit"
                disabled={submitting || code.length !== 6}
                className="w-full bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground font-medium py-2.5 rounded transition flex items-center justify-center gap-2"
              >
                {submitting ? 'Verifying…' : 'Verify & create account'}
              </button>
            </form>
            <div className="mt-4 flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={backToForm}
                className="text-muted-foreground hover:text-foreground transition flex items-center gap-1"
              >
                <ArrowLeft size={14} /> Change details
              </button>
              <button
                type="button"
                onClick={handleResend}
                disabled={resendIn > 0}
                className="text-primary hover:underline disabled:text-muted-foreground disabled:no-underline disabled:cursor-default"
              >
                {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
